// Verwijzingen naar producten in upsells en cross-sells.
//
// Het importbestand verwijst met kleur-SKU's (TC-010-ZWA). Zo'n SKU wijst niet
// naar een product maar naar één kleurvariant daarvan. Werd dat teruggebracht tot
// alleen de product-slug, dan viel de kleur weg: alle kleuren van een product
// kregen dezelfde lijst, verwijzingen naar zusterkleuren gingen op tegen de
// zelf-verwijzingsfilter, en in de backend bleef alleen "TC-010" over.
//
// Daarom slaan we een verwijzing op als "slug?kleur=<kleurslug>" — hetzelfde
// formaat als de deep-link op de site, dus ook leesbaar in de admin.

export interface ProductRef {
  slug: string;
  colorSlug?: string;
}

export function parseRef(raw: string): ProductRef {
  const s = String(raw || "").trim();
  if (!s) return { slug: "" };
  const i = s.indexOf("?kleur=");
  if (i === -1) return { slug: s.toLowerCase() };
  return {
    slug: s.slice(0, i).toLowerCase(),
    colorSlug: decodeURIComponent(s.slice(i + 7)).toLowerCase() || undefined,
  };
}

export function formatRef({ slug, colorSlug }: ProductRef): string {
  if (!slug) return "";
  return colorSlug ? `${slug}?kleur=${colorSlug}` : slug;
}

/** Alleen het product-deel — voor het ophalen van het document. */
export function refSlug(raw: string): string {
  return parseRef(raw).slug;
}

/** Link naar de productpagina, met de juiste kleur voorgeselecteerd — de
 *  crawlbare pad-URL (/product/{slug}/{kleur}), niet het interne
 *  opslagformaat hierboven (dat blijft ?kleur=, puur voor dataverwijzingen). */
export function refHref(raw: string): string {
  const { slug, colorSlug } = parseRef(raw);
  return colorSlug ? `/product/${slug}/${encodeURIComponent(colorSlug)}` : `/product/${slug}`;
}

/**
 * Kiest de kleuroptie waar een verwijzing op doelt. Zonder kleur (of bij een
 * onbekende kleur) valt hij terug op het product zelf.
 */
export function pickColorOption<T extends { slug?: string; name?: string }>(
  colorOptions: T[] | undefined,
  colorSlug?: string
): T | undefined {
  if (!colorSlug || !colorOptions?.length) return undefined;
  const norm = (s?: string) => String(s || "").trim().toLowerCase();
  return colorOptions.find((c) => norm(c.slug) === colorSlug || norm(c.name).replace(/\s+/g, "-") === colorSlug);
}

/**
 * Kiest, ONgericht (de verwijzing zelf schrijft geen kleur voor), de kleuroptie
 * die het best matcht met een lijst gewenste kleurnamen — bijv. de kleuren die
 * al in de winkelwagen liggen, zodat een upsell/cross-sell zonder eigen
 * kleurkoppeling niet zomaar de eerste/default kleur toont maar een passende.
 * Eerst exacte naam-match, dan match op hoofdkleur (zodat "Donkerrood" in de
 * mand ook "Rood" bij het voorgestelde product matcht).
 */
export function pickMatchendeKleur<T extends { name?: string; mainColor?: string }>(
  colorOptions: T[] | undefined,
  gewensteKleuren: string[]
): T | undefined {
  if (!colorOptions?.length || !gewensteKleuren.length) return undefined;
  const norm = (s?: string) => String(s || "").trim().toLowerCase();
  const gewenst = new Set(gewensteKleuren.map(norm));
  return (
    colorOptions.find((c) => gewenst.has(norm(c.name))) ||
    colorOptions.find((c) => gewenst.has(norm(c.mainColor)))
  );
}
